import { useSnackbar } from 'notistack';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  UseFieldArrayReturn,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useHasChanged } from '~/hooks/useHasChanged';

import {
  DocumentType,
  getCompanyDocuments,
  getEmployeeDocuments,
} from './data';
import { mapDefaultData } from './data/dataMapping';
import { dataSaving } from './data/dataSaving';

export type FixForKeyIssue = {
  [key: string]: number | string | null | boolean | File | undefined;
};

export interface RowData extends FixForKeyIssue {
  fDocumentCreated?: string;
  fDocumentDescription?: string;
  fDocumentDocType: string;
  fDocumentExpirationDate?: string;
  fDocumentId: number;
  fDocumentModified?: string;
  fDocumentName?: string;
  fDocumentAttachment?: File | undefined;
}

export type IFormInput = {
  document: RowData[];
};

export type UseDocumentsOutput = {
  companyDoc: DocumentType[];
  useFormMethods: UseFormReturn<IFormInput>;
  docEnumType: Enum[];
  docEnumStatus: Enum[];
  docList: DocumentType[];
  setEditedRows: Dispatch<SetStateAction<number[]>>;
  setDeletedRows: Dispatch<SetStateAction<number[]>>;
  editMode: boolean;
  defaultData: RowData[];
  setEditMode: Dispatch<SetStateAction<boolean>>;
  onSubmit: () => void;
  useFieldArrayMethods: UseFieldArrayReturn<IFormInput, 'document', 'id'>;
  isLoading: boolean;
};

const useGetEmployeeDocuments = (employeeId: number) => {
  const { data, ...rest } = useSWR(['employeeDocuments', employeeId], () =>
    getEmployeeDocuments(employeeId.toString()).then((data) => {
      return data.sort((a: DocumentType, b: DocumentType) => {
        return (
          new Date(b.fDocumentCreated).valueOf() -
          new Date(a.fDocumentCreated).valueOf()
        );
      });
    }),
  );

  return {
    data: data ?? [],
    ...rest,
  };
};

const useGetCompanyDocuments = () => {
  const { data, ...rest } = useSWR(['companyDocuments'], () =>
    getCompanyDocuments(),
  );

  return {
    data: data ?? [],
    ...rest,
  };
};

export const useDocuments = (): UseDocumentsOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [editMode, setEditMode] = useState<boolean>(false);

  const { getEnums } = useAllEnums();
  const enumLists = useMemo(() => {
    return {
      docEnumType: getEnums(EnumListKey.DOCUMENT_TYPE),
      docEnumStatus: getEnums(EnumListKey.DOCUMENT_APPROVAL_STATUS),
    };
  }, [getEnums]);

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const {
    data: documentData,
    isLoading: isLoadingDocumentsData,
    mutate: mutateDocumentsData,
  } = useGetEmployeeDocuments(selectedEmployeeId);
  const {
    data: docList,
    isLoading: isLoadingDocList,
    mutate: mutateDocList,
  } = useGetCompanyDocuments();

  const isLoading = isLoadingDocumentsData || isLoadingDocList;

  const defaultData = useMemo(
    () => mapDefaultData(documentData),
    [documentData],
  );
  const hasDefaultDataChanged = useHasChanged(JSON.stringify(defaultData));

  const useFormMethods = useForm<IFormInput>();
  const { handleSubmit, control, reset } = useFormMethods;

  useEffect(() => {
    if (hasDefaultDataChanged) {
      reset({
        document: defaultData,
      });
    }
  }, [hasDefaultDataChanged, defaultData, reset]);

  const [editedRows, setEditedRows] = useState<number[]>([]);
  const [deletedRows, setDeletedRows] = useState<number[]>([]);

  const useFieldArrayMethods = useFieldArray({ name: 'document', control });

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const rowData = data.document;

      return dataSaving(
        selectedEmployeeId,
        deletedRows,
        editedRows,
        defaultData,
        rowData,
      )
        .then(() => {
          mutateDocumentsData();
          mutateDocList();
          setEditMode(!editMode);
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
        })
        .catch((_err) => {
          enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
        });
    });
  }, [
    handleSubmit,
    selectedEmployeeId,
    deletedRows,
    editedRows,
    defaultData,
    mutateDocumentsData,
    mutateDocList,
    editMode,
    enqueueSnackbar,
    t,
  ]);

  return {
    defaultData,
    useFormMethods,
    companyDoc: documentData,
    ...enumLists,
    docList,
    setEditedRows,
    setDeletedRows,
    editMode,
    setEditMode,
    onSubmit: handleFormSubmit,
    useFieldArrayMethods,
    isLoading,
  };
};
